import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { useSEO } from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { GoogleLogo, EnvelopeSimple, CircleNotch, CheckCircle, WarningCircle, Headset } from "@phosphor-icons/react";

const NOTICES = {
  account_suspended: "This account has been suspended. Contact your Controlly administrator.",
  signup_disabled: "There's no Controlly account for that sign-in. Ask your organization administrator for an invitation.",
};

const consumeNotice = () => {
  const q = new URLSearchParams(window.location.search).get("notice");
  let n = q;
  try { n = n || sessionStorage.getItem("controlly_notice"); sessionStorage.removeItem("controlly_notice"); } catch { /* ignore */ }
  return NOTICES[n] || null;
};

const EmailLinkForm = () => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true); setErr("");
    try { await api.post("/auth/magic-link/request", { email: email.trim() }); setSent(true); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Something went wrong. Please try again."); }
    finally { setSending(false); }
  };
  if (sent) return (
    <div data-testid="magic-link-sent" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
      <CheckCircle size={28} weight="fill" className="text-emerald-500 mx-auto mb-2" />
      <p className="text-sm text-zinc-200">If <span className="font-semibold">{email}</span> has access, a sign-in link is on its way.</p>
      <p className="text-xs text-zinc-500 mt-1">The link expires in 20 minutes.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="magic-link-form">
      <input data-testid="login-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
        className="w-full h-11 rounded-md bg-white/5 border border-white/10 px-3.5 text-sm text-white placeholder:text-zinc-500 focus:border-blue-500 focus:outline-none transition-colors" />
      {err && <p data-testid="login-error" className="text-xs text-red-400">{err}</p>}
      <Button type="submit" data-testid="magic-link-submit" disabled={!email.trim() || sending} variant="outline"
        className="w-full h-11 bg-transparent border-white/15 hover:bg-white/5 text-white font-medium rounded-md disabled:opacity-40">
        {sending ? <CircleNotch size={18} className="animate-spin" /> : <><EnvelopeSimple size={18} className="mr-2" /> Email me a sign-in link</>}
      </Button>
    </form>
  );
};

export default function PrivateLogin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [notice] = useState(consumeNotice);
  useSEO({ title: "Sign in — Controlly", description: "Controlly private remote-support platform.", path: "/login", robots: "noindex, nofollow" });

  useEffect(() => {
    if (!loading && user) {
      let dest = "/dashboard";
      try {
        const next = localStorage.getItem("controlly_next");
        if (next && next.startsWith("/") && !next.startsWith("//")) dest = next;
        localStorage.removeItem("controlly_next");
      } catch { /* ignore */ }
      navigate(dest, { replace: true });
    }
  }, [user, loading, navigate]);

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#07090d] flex flex-col" data-testid="private-login-page">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="flex items-center gap-3 mb-10">
            <img src="/controlly-icon.png" alt="Controlly" className="w-11 h-11 rounded-md drop-shadow-[0_2px_10px_rgba(37,120,255,0.45)]" />
            <div>
              <div className="font-heading text-lg font-extrabold tracking-tight leading-none">Controlly</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Remote support platform</div>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Sign in</h1>
          <p className="text-sm text-zinc-400 mt-2">Access is limited to authorized team members.</p>

          {notice && (
            <div data-testid="login-notice" className="mt-6 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
              <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5" /> {notice}
            </div>
          )}

          <Button data-testid="google-login-button" onClick={google} className="w-full mt-8 bg-white text-black hover:bg-zinc-200 font-medium h-11 rounded-md">
            <GoogleLogo size={19} weight="bold" className="mr-2" /> Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-widest">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <EmailLinkForm />

          <p className="text-[11px] text-zinc-600 mt-6 leading-relaxed">
            By signing in you agree to the <Link to="/terms" className="underline underline-offset-2 hover:text-zinc-400">Terms</Link> and{" "}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-zinc-400">Privacy Policy</Link>, and confirm you're authorized to access any computer you connect to.
          </p>

          <Link to="/support" data-testid="login-support-code-link"
            className="mt-8 flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 hover:border-blue-500/40 hover:bg-blue-500/5 transition-colors">
            <Headset size={20} className="text-blue-400 shrink-0" />
            <span className="text-sm"><span className="text-zinc-200 font-medium">Join a support session</span><span className="block text-xs text-zinc-500">Have a code from your technician?</span></span>
          </Link>
        </div>
      </main>
      <footer className="px-5 py-5 text-center text-[11px] text-zinc-600 font-mono" data-testid="login-footer">
        Operated by Epicom Systems Ltd.
      </footer>
    </div>
  );
}
